import type { JSX } from "solid-js";

const DEFAULT_DURATION = 500;

interface AnimatedSectionProps {
  /**
   * Whether the section should be visible and expanded.
   */
  show: boolean;
  /**
   * Content to render inside the animated section.
   */
  children: JSX.Element;
  /**
   * Optional CSS class for the container.
   */
  class?: string;
  /**
   * Duration of the transition in milliseconds.
   * @default 500
   */
  duration?: number;
}

/**
 * Ingenious CSS-only height and opacity transition component.
 * Uses grid-template-rows to animate from 0 to 'auto' height without JS calculations.
 * This is highly performant on mobile as it relies on native layout transitions.
 */
export const AnimatedSection = (props: AnimatedSectionProps) => {
  const duration = () => props.duration ?? DEFAULT_DURATION;

  return (
    <div
      // Collapsed = hidden from assistive technology and out of the tab
      // order: the section stays in the DOM (it animates with grid 0fr +
      // opacity, it is not unmounted), so without this a screen reader would
      // announce invisible content and focus could land on hidden controls.
      // `aria-hidden` reflects `show` reliably (unlike an accidental ancestor).
      aria-hidden={!props.show}
      class={`grid transition-[grid-template-rows,opacity] ease-in-out ${props.class ?? ""}`}
      style={{
        "grid-template-rows": props.show ? "1fr" : "0fr",
        opacity: props.show ? 1 : 0,
        "transition-duration": `${duration()}ms`,
      }}
    >
      <div class="overflow-hidden">{props.children}</div>
    </div>
  );
};
